import type {Uuid} from "@vesta/common-type-definitions/typeDefinitions";
import {dataResult, type Result} from "@vesta/error-handling-remix/result";
import {uuidZodValidator} from "@vesta/global-common-typescript/validators/zodValidators";
import {
    type FitmentMasterNode,
    fitmentMasterNodeValidator,
} from "@vesta/vos-types/modules/workspaceLevel/workspaceCapabilities/flexibleFitmentEngineWorkspaceCapability/domain/FitmentMasterNode";
import {
    type VariantCompatibilityLink,
    variantCompatibilityLinkValidator,
} from "@vesta/vos-types/modules/workspaceLevel/workspaceCapabilities/flexibleFitmentEngineWorkspaceCapability/domain/VariantCompatibilityLink";
import {plutoPost, type PlutoWorkspaceClientContext} from "~/utilities/ottokart/plutoClient";
import {z as zod} from "zod";

const COMPATIBILITY_LINK_LIST_LIMIT = 10_000;

export type FitmentMasterNodeListColumn = "displayName" | "machineTypeId" | "manufacturerId" | "slug";

export type FitmentMasterNodeListFilters = {
    createdAtFrom?: string | null;
    createdAtTo?: string | null;
    displayNames?: Array<string>;
    machineTypeIds?: Array<string>;
    manufacturerIds?: Array<string>;
    slugs?: Array<string>;
};

function fitmentMasterNodeFiltersBody(
    filters: FitmentMasterNodeListFilters | undefined,
): {filters: FitmentMasterNodeListFilters} | Record<string, never> {
    if (filters === undefined) {
        return {};
    }
    const requestFilters: FitmentMasterNodeListFilters = {};
    if (filters.displayNames !== undefined) {
        requestFilters.displayNames = filters.displayNames;
    }
    if (filters.machineTypeIds !== undefined) {
        requestFilters.machineTypeIds = filters.machineTypeIds;
    }
    if (filters.manufacturerIds !== undefined) {
        requestFilters.manufacturerIds = filters.manufacturerIds;
    }
    if (filters.slugs !== undefined) {
        requestFilters.slugs = filters.slugs;
    }
    if (filters.createdAtFrom !== undefined && filters.createdAtTo !== undefined) {
        requestFilters.createdAtFrom = filters.createdAtFrom;
        requestFilters.createdAtTo = filters.createdAtTo;
    }
    if (
        requestFilters.displayNames === undefined &&
        requestFilters.machineTypeIds === undefined &&
        requestFilters.manufacturerIds === undefined &&
        requestFilters.slugs === undefined &&
        requestFilters.createdAtFrom === undefined &&
        requestFilters.createdAtTo === undefined
    ) {
        return {};
    }
    return {filters: requestFilters};
}

export async function listFitmentMasterNodes({
    filters,
    limit,
    nodeType,
    offset,
    parentId,
    search,
    ...context
}: PlutoWorkspaceClientContext & {
    filters?: FitmentMasterNodeListFilters;
    limit?: number;
    nodeType?: string;
    offset?: number;
    parentId?: Uuid | null;
    search?: string;
}): Promise<Result<Array<FitmentMasterNode>>> {
    const trimmedSearch = search?.trim() ?? "";
    return plutoPost({
        ...context,
        path: "/fitment-master-nodes/get-all",
        body: {
            ...(limit !== undefined && offset !== undefined
                ? {
                      limit: limit,
                      offset: offset,
                  }
                : {}),
            nodeType: nodeType,
            parentId: parentId,
            ...(trimmedSearch.length > 0 ? {search: trimmedSearch} : {}),
            ...fitmentMasterNodeFiltersBody(filters),
        },
        responseValidator: zod.array(fitmentMasterNodeValidator),
    });
}

export async function countFitmentMasterNodes({
    filters,
    nodeType,
    parentId,
    search,
    ...context
}: PlutoWorkspaceClientContext & {
    filters?: FitmentMasterNodeListFilters;
    nodeType?: string;
    parentId?: Uuid | null;
    search?: string;
}): Promise<Result<number>> {
    const trimmedSearch = search?.trim() ?? "";
    return plutoPost({
        ...context,
        path: "/fitment-master-nodes/get-all",
        body: {
            countOnly: true,
            nodeType: nodeType,
            parentId: parentId,
            ...(trimmedSearch.length > 0 ? {search: trimmedSearch} : {}),
            ...fitmentMasterNodeFiltersBody(filters),
        },
        responseValidator: zod.coerce.number().int().nonnegative(),
    });
}

export async function listFitmentMasterNodeColumnValues({
    column,
    filters,
    nodeType,
    parentId,
    search,
    ...context
}: PlutoWorkspaceClientContext & {
    column: FitmentMasterNodeListColumn;
    filters?: FitmentMasterNodeListFilters;
    nodeType?: string;
    parentId?: Uuid | null;
    search?: string;
}): Promise<Result<Array<string>>> {
    const trimmedSearch = search?.trim() ?? "";
    return plutoPost({
        ...context,
        path: "/fitment-master-nodes/get-all",
        body: {
            columnValuesFor: column,
            nodeType: nodeType,
            parentId: parentId,
            ...(trimmedSearch.length > 0 ? {search: trimmedSearch} : {}),
            ...fitmentMasterNodeFiltersBody(filters),
        },
        responseValidator: zod.array(zod.string()),
    });
}

export async function getFitmentMasterNodeById({
    id,
    ...context
}: PlutoWorkspaceClientContext & {
    id: string;
}): Promise<Result<FitmentMasterNode>> {
    return plutoPost({
        ...context,
        path: "/fitment-master-nodes/get",
        body: {
            id: id,
        },
        responseValidator: fitmentMasterNodeValidator,
    });
}

export async function createFitmentMasterNode({
    attributes,
    displayName,
    id,
    nodeType,
    parentId,
    ...context
}: PlutoWorkspaceClientContext & {
    attributes?: Record<string, unknown> | null;
    displayName: string;
    id?: Uuid;
    nodeType: string;
    parentId?: Uuid | null;
}): Promise<Result<Uuid>> {
    return plutoPost({
        ...context,
        path: "/fitment-master-nodes/create",
        body: {
            attributes: attributes,
            displayName: displayName,
            id: id,
            nodeType: nodeType,
            parentId: parentId ?? null,
        },
        responseValidator: uuidZodValidator,
    });
}

export async function updateFitmentMasterNode({
    attributes,
    displayName,
    id,
    nodeType,
    parentId,
    ...context
}: PlutoWorkspaceClientContext & {
    attributes?: Record<string, unknown> | null;
    displayName?: string;
    id: Uuid;
    nodeType?: string;
    parentId?: Uuid | null;
}): Promise<Result<unknown>> {
    return plutoPost({
        ...context,
        path: "/fitment-master-nodes/update",
        body: {
            attributes: attributes,
            displayName: displayName,
            id: id,
            nodeType: nodeType,
            parentId: parentId,
        },
        responseValidator: zod.unknown(),
    });
}

export async function deleteFitmentMasterNode({
    id,
    ...context
}: PlutoWorkspaceClientContext & {
    id: string;
}): Promise<Result<unknown>> {
    return plutoPost({
        ...context,
        path: "/fitment-master-nodes/delete",
        body: {
            id: id,
        },
        responseValidator: zod.unknown(),
    });
}

export async function listVariantCompatibilityLinks({
    limit,
    masterNodeId,
    offset,
    variantId,
    ...context
}: PlutoWorkspaceClientContext & {
    limit?: number;
    masterNodeId?: Uuid;
    offset?: number;
    variantId?: Uuid;
}): Promise<Result<Array<VariantCompatibilityLink>>> {
    return plutoPost({
        ...context,
        path: "/variant-compatibility-links/get-all",
        body: {
            limit: limit,
            masterNodeId: masterNodeId,
            offset: offset,
            variantId: variantId,
        },
        responseValidator: zod.array(variantCompatibilityLinkValidator),
    });
}

export async function listAllVariantCompatibilityLinks(
    context: PlutoWorkspaceClientContext,
): Promise<Result<Array<VariantCompatibilityLink>>> {
    return listVariantCompatibilityLinks({
        ...context,
        limit: COMPATIBILITY_LINK_LIST_LIMIT,
        offset: 0,
    });
}

export async function createVariantCompatibilityLink({
    masterNodeId,
    metadata,
    variantId,
    ...context
}: PlutoWorkspaceClientContext & {
    masterNodeId: Uuid;
    metadata?: Record<string, unknown> | null;
    variantId: Uuid;
}): Promise<Result<unknown>> {
    return plutoPost({
        ...context,
        path: "/variant-compatibility-links/create",
        body: {
            masterNodeId: masterNodeId,
            metadata: metadata,
            variantId: variantId,
        },
        responseValidator: zod.unknown(),
    });
}

export async function deleteVariantCompatibilityLink({
    id,
    ...context
}: PlutoWorkspaceClientContext & {
    id: Uuid;
}): Promise<Result<unknown>> {
    return plutoPost({
        ...context,
        path: "/variant-compatibility-links/delete",
        body: {
            id: id,
        },
        responseValidator: zod.unknown(),
    });
}

export async function buildCompatibilityLinksByVariantIdMap(
    context: PlutoWorkspaceClientContext,
): Promise<Result<Map<Uuid, Array<Uuid>>>> {
    const linksResult = await listAllVariantCompatibilityLinks(context);
    if (linksResult.success !== true) {
        return linksResult;
    }

    const linksByVariantId = new Map<Uuid, Array<Uuid>>();
    for (const link of linksResult.data) {
        const existingMasterNodeIds = linksByVariantId.get(link.productCatalogueId) ?? [];
        existingMasterNodeIds.push(link.masterNodeId);
        linksByVariantId.set(link.productCatalogueId, existingMasterNodeIds);
    }

    return dataResult(linksByVariantId);
}

export async function syncVariantCompatibilityLinks({
    masterNodeIds,
    variantId,
    ...context
}: PlutoWorkspaceClientContext & {
    masterNodeIds: Array<Uuid>;
    variantId: Uuid;
}): Promise<Result<void>> {
    const existingLinksResult = await listVariantCompatibilityLinks({
        ...context,
        variantId: variantId,
    });
    if (existingLinksResult.success !== true) {
        return existingLinksResult;
    }

    const desiredMasterNodeIds = new Set(masterNodeIds);
    const existingMasterNodeIds = new Set(
        existingLinksResult.data.map((link) => {
            return link.masterNodeId;
        }),
    );

    for (const link of existingLinksResult.data) {
        if (desiredMasterNodeIds.has(link.masterNodeId) !== true) {
            const deleteResult = await deleteVariantCompatibilityLink({
                ...context,
                id: link.id,
            });
            if (deleteResult.success !== true) {
                return deleteResult;
            }
        }
    }

    for (const masterNodeId of masterNodeIds) {
        if (existingMasterNodeIds.has(masterNodeId) !== true) {
            const createResult = await createVariantCompatibilityLink({
                ...context,
                masterNodeId: masterNodeId,
                variantId: variantId,
            });
            if (createResult.success !== true) {
                return createResult;
            }
        }
    }

    return dataResult(undefined);
}
